import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
} from "@mui/material";

import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { getIconDirection } from "modules/getIconDirection";
import config from "config";

mapboxgl.accessToken = config.MAPBOX_ACCESS_TOKEN_3D;
const mapStyle = config.MAP_3D;

import {
  fetchJunctionList,
  fetchJunctionData,
  fetchRcmList,
  fetchRcmData,
} from "./data/fetchIntersection";
import {
  convertNewGeo,
  normalizeJunction,
  normalizeRcm,
  sortGeomJunction,
} from "./data/transformIntersection";

export default function TryIntersection() {
  // ── STATE DECLARATIONS ──────────────────────────────────────────────
  const [mode, setMode] = useState("junction");
  const [junctionList, setJunctionList] = useState([]);
  const [junctionData, setJunctionData] = useState(null);
  const [rcmList, setRcmList] = useState([]);
  const [rcmData, setRcmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [selectedFlow, setSelectedFlow] = useState(null);

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const hasZoomedToFlow = useRef(false);

  const [initViewState, setInitViewState] = useState({
    center: [22.96482, 40.59718],
    zoom: 15,
    pitch: 63,
    bearing: 0,
  });

  // Helper to pick first ID
  function initializeSelection(currentMode, normJList, normRList) {
    if (currentMode === "junction" && normJList.length > 0) {
      return normJList[0].id;
    }
    if (currentMode === "rcm" && normRList.length > 0) {
      return normRList[0].id;
    }
    return "";
  }

  // Fetch lists and raw GeoJSON once
  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      setLoading(true);
      try {
        const [rawJList, rawRList, rawJGeo, rawRGeo] = await Promise.all([
          fetchJunctionList(),
          fetchRcmList(),
          fetchJunctionData(),
          fetchRcmData(),
        ]);
        if (!isMounted) return;

        const normJList = normalizeJunction(rawJList);
        const normRList = normalizeRcm(rawRList);

        // console.log("rawJGeo", sortGeomJunction(rawRGeo));
        // console.log("convertNewGeo", convertNewGeo(rawJGeo))

        const convertJGeo = convertNewGeo(rawJGeo);

        console.log("convertJGeo", convertJGeo);

        setJunctionList(normJList);
        setRcmList(normRList);
        setJunctionData(convertJGeo);
        setRcmData(rawRGeo);

        const initialId = initializeSelection(mode, normJList, normRList);

        // console.log("initialId", initialId);

        setSelectedItemId(initialId);
      } catch (err) {
        console.error("Error loading lists or GeoJSON:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Re-initialize selection when mode or lists change
  useEffect(() => {
    const newId = initializeSelection(mode, junctionList, rcmList);
    setSelectedItemId(newId);
  }, [mode, junctionList, rcmList]);

  const itemsToShow = mode === "junction" ? junctionList : rcmList;

  // Compute filtered GeoJSON
  const filteredGeoJson = useMemo(() => {
    if (!selectedItemId) {
      return { type: "FeatureCollection", features: [] };
    }

    const data = mode === "junction" ? junctionData : rcmData;
    if (!data || !Array.isArray(data.features)) {
      return { type: "FeatureCollection", features: [] };
    }

    let feats = data.features.filter(
      (f) => f.properties.junction === selectedItemId
    );
    if (selectedFlow !== null) {
      feats = feats.filter((f) => f.properties.flow === selectedFlow);
    }
    return { type: "FeatureCollection", features: feats };
  }, [mode, junctionData, rcmData, selectedItemId, selectedFlow]);

  // Derive available flows
  const flowsForSelected = useMemo(() => {
    if (!selectedItemId) return [];
    const data = mode === "junction" ? junctionData : rcmData;
    if (!data || !Array.isArray(data.features)) return [];

    const feats = data.features.filter(
      (f) => f.properties.junction === selectedItemId
    );
    return Array.from(new Set(feats.map((f) => f.properties.flow)));
  }, [mode, junctionData, rcmData, selectedItemId]);

  // Update selectedFlow when flows change
  useEffect(() => {
    if (flowsForSelected.length > 0) {
      setSelectedFlow(flowsForSelected[0]);
    } else {
      setSelectedFlow(null);
    }
  }, [flowsForSelected]);

  // Extract viewState (optional)
  const viewState = useMemo(() => {
    if (!filteredGeoJson.features.length) return null;
    return filteredGeoJson.features[0].properties.viewState || null;
  }, [filteredGeoJson]);

  // Initialize map once data & container ready
  useEffect(() => {
    if (loading) return;
    if (!containerRef.current) return;
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: mapStyle,
      center: initViewState.center,
      zoom: initViewState.zoom,
      pitch: initViewState.pitch,
      bearing: initViewState.bearing,
    });
    mapRef.current = map;

    map.on("load", async () => {
      // Load directional icons
      await Promise.all(
        [1, 2, 3, 4].map(
          (code) =>
            new Promise((resolve, reject) => {
              map.loadImage(getIconDirection(code), (err, img) => {
                if (err) reject(err);
                else {
                  map.addImage(`direction-${code}`, img);
                  resolve();
                }
              });
            })
        )
      );

      // console.log('filteredGeoJson', filteredGeoJson);

      // Add filtered-data source
      map.addSource("filtered-data", {
        type: "geojson",
        data: filteredGeoJson,
      });

      // Arrow symbols
      map.addLayer({
        id: "layer-arrows",
        type: "symbol",
        source: "filtered-data",
        layout: {
          "icon-image": [
            "concat",
            "direction-",
            ["to-string", ["get", "direction"]],
          ],
          "icon-size": 1,
          "icon-allow-overlap": true,
        },
      });

      // Vehicle count text
      map.addLayer({
        id: "layer-vehicles",
        type: "symbol",
        source: "filtered-data",
        layout: {
          "text-field": ["to-string", ["get", "metric_value"]],
          "text-size": 18,
          "text-variable-anchor": ["top", "right"],
          "text-radial-offset": 0.6,
          "text-justify": "auto",
          "text-allow-overlap": false, // let Mapbox decide best placement
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "#000000",
          "text-halo-width": 4,
        },
      });
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [loading, containerRef.current]);

  // Update source data when filteredGeoJson changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const source = map.getSource("filtered-data");
    if (source) {
      source.setData(filteredGeoJson);
    }
  }, [filteredGeoJson]);

  // Zoom to flow on first load
  useEffect(() => {
    const map = mapRef.current;
    if (!map || filteredGeoJson.features.length === 0) return;

    // console.log("zoom ", filteredGeoJson.features[0]?.properties.viewState);
    const flyViewState = filteredGeoJson.features[0]?.properties.viewState;

    // const coords = filteredGeoJson.features.map((f) => f.geometry.coordinates);
    // const lons = coords.map((c) => c[0]);
    // const lats = coords.map((c) => c[1]);

    // const bounds = [
    //   [Math.min(...lons), Math.min(...lats)],
    //   [Math.max(...lons), Math.max(...lats)],
    // ];

    // map.fitBounds(bounds, { padding: 50, duration: 1000 });
    // hasZoomedToFlow.current = true;
    map.flyTo({
      center: flyViewState.center,
      zoom: flyViewState.zoom,
      pitch: 80,
      bearing: flyViewState.bearing ?? 0,
      duration: 800,
      speed: 1.6,
      curve: 1.42,
      essential: true,
    });
  }, [filteredGeoJson]);

  // Early return while loading
  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  // Handlers
  const handleModeChange = (e) => setMode(e.target.value);
  const handleFlowChange = (e) => setSelectedFlow(e.target.value);

  return (
    <DashboardLayout>
      <Card sx={{ mt: 2, position: "relative", height: "85vh" }}>
        <MDBox
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            width: { xs: "100%", sm: 260 },
            bgcolor: "rgba(32,41,64,0.9)",
            p: 2,
            borderRadius: 1,
            zIndex: 10,
          }}
        >
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="mode-label" sx={{ color: "#fff" }}>
              Data Type
            </InputLabel>
            <Select
              labelId="mode-label"
              id="mode-select"
              value={mode}
              onChange={handleModeChange}
              sx={{ color: "#fff" }}
            >
              <MenuItem value="junction">Junctions</MenuItem>
              <MenuItem value="rcm">RCM</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="item-label" sx={{ color: "#fff" }}>
              {mode === "junction" ? "Select Junction" : "Select RCM"}
            </InputLabel>
            <Select
              labelId="item-label"
              id="item-select"
              value={selectedItemId}
              onChange={(e) => setSelectedItemId(e.target.value)}
              sx={{ color: "#fff" }}
            >
              {itemsToShow.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.id}: {item.name_en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {flowsForSelected.length > 0 && (
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel id="flow-label" sx={{ color: "#fff" }}>
                Select Flow
              </InputLabel>
              <Select
                labelId="flow-label"
                id="flow-select"
                value={selectedFlow ?? ""}
                onChange={handleFlowChange}
                sx={{ color: "#fff" }}
              >
                {flowsForSelected.map((flow) => (
                  <MenuItem key={flow} value={flow}>
                    Flow {flow}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </MDBox>

        <div
          ref={containerRef}
          style={{ width: "100%", height: "100%", position: "relative" }}
        />
      </Card>
    </DashboardLayout>
  );
}
